
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Proposal } from "@shared/schema";
import { format } from "date-fns";
import { useMemo, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Pencil, Trash2, Eye } from "lucide-react";

interface ProposalCardProps {
  proposal: Proposal;
  onEdit: (id: number) => void;
  onView: (id: number) => void;
  onDelete: (id: number) => void;
  isDeleting: boolean;
}

const formatDate = (date: Date | string): string => {
  try {
    return format(new Date(date), "MMM d, yyyy");
  } catch {
    return "Unknown date";
  }
};

const renderStatusBadge = (status: string) => {
  const normalized = status.toLowerCase();

  switch (normalized) {
    case 'draft':
      return <Badge variant="outline">Draft</Badge>;
    case 'sent':
      return <Badge variant="secondary">Sent</Badge>;
    case 'approved':
      return <Badge variant="secondary">Approved</Badge>;
    case 'rejected':
      return <Badge variant="destructive">Rejected</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
};

export function ProposalCard({ proposal, onEdit, onView, onDelete, isDeleting }: ProposalCardProps) {
  const { toast } = useToast();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const highlights = useMemo(() => {
    if (Array.isArray(proposal.highlights)) {
      return proposal.highlights.filter((highlight): highlight is string => typeof highlight === "string");
    }

    return [];
  }, [proposal.highlights]);

  const handleDeleteConfirm = () => {
    setDeleteDialogOpen(false);
    onDelete(proposal.id);
    toast({
      title: "Proposal deleted",
      description: "The proposal has been deleted successfully.",
    });
  };

  return (
    <Card className="w-full">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-lg font-bold">{proposal.title}</CardTitle>
            <CardDescription>Created on {formatDate(proposal.createdAt)}</CardDescription>
          </div>
          <div>{renderStatusBadge(proposal.status)}</div>
        </div>
      </CardHeader>
      <CardContent className="pb-2">
        <div className="space-y-2">
          <div>
            <h4 className="font-medium text-sm">Highlights</h4>
            <ul className="list-disc pl-5 text-sm">
              {highlights.slice(0, 3).map((highlight, index) => (
                <li key={index}>{highlight}</li>
              ))}
              {highlights.length > 3 && (
                <li>
                  <span className="text-muted-foreground">+{highlights.length - 3} more...</span>
                </li>
              )}
            </ul>
          </div>
          {proposal.advisorNote && (
            <div>
              <h4 className="font-medium text-sm">Advisor Note</h4>
              <p className="text-sm line-clamp-2 text-muted-foreground">{proposal.advisorNote}</p>
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter className="flex justify-between pt-4">
        <Button variant="outline" size="sm" onClick={() => onView(proposal.id)}>
          <Eye className="h-4 w-4 mr-2" />
          View
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => onEdit(proposal.id)}>
            <Pencil className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm" disabled={isDeleting}>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  This action cannot be undone. This will permanently delete the proposal and all associated documents.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDeleteConfirm} className="bg-destructive text-destructive-foreground">
                  {isDeleting ? "Deleting..." : "Delete"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardFooter>
    </Card>
  );
}
